import reportitemproductionService from '../../../services/reportitemproductionService';
import filterService from '../../../services/filterService';

import {
    setReportitemproductionLoading,
    setReportitemproductionError,
    setReportitemproductionList,
    setReportitemproductionCurrent,
    setReportitemproductionPagination,
    setReportitemproductionSort,
    setReportitemproductionFilters,
    setReportitemproductionDefaultFilter,
    setReportitemproductionSettings,
    clearReportitemproductionCurrent,
    clearReportitemproductionError,
    setReportitemproductionFilterLoading,
    changeReportitemproductionFilterCondition,
    addReportitemproductionFilterCondition,
    updateReportitemproductionFilterConditionRule,
    updateReportitemproductionFilterConditionValue,
    removeReportitemproductionFilterCondition,
    setReportitemproductionSelected,
    setReportitemproductionDownloading,
    resetReportitemproductionFiltersAndPagination,

    setReportitemproductionClassicFilters
} from './reportitemproductionSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportitemproduction.isSettingsInitialized) {
            dispatch(setReportitemproductionLoading(true));
            const response = await reportitemproductionService.getSettings();

            // Set the settings
            dispatch(setReportitemproductionSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportitemproductionClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportitemproductionDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportitemproductionError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportitemproductionLoading(false));
    }
};

export const fetchReportitemproductions = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportitemproduction;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportitemproductionLoading(true));
        const response = await reportitemproductionService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportitemproductionList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportitemproductions:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportitemproductionError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportitemproductionLoading(false));
    }
};

export const fetchReportitemproduction = (id) => async (dispatch) => {
    try {
        dispatch(setReportitemproductionLoading(true));
        const response = await reportitemproductionService.getOne(id);
        dispatch(setReportitemproductionCurrent(response.data));
    } catch (error) {
        dispatch(setReportitemproductionError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportitemproductionLoading(false));
    }
};

export const createReportitemproduction = (data) => async (dispatch) => {
    try {
        const response = await reportitemproductionService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportitemproduction = (id, data) => async (dispatch) => {
    try {
        const response = await reportitemproductionService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportitemproduction = (id) => async (dispatch) => {
    try {
        dispatch(setReportitemproductionLoading(true));
        const response = await reportitemproductionService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportitemproductionError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportitemproductionLoading(false));
    }
};

export const downloadReportitemproduction = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportitemproduction;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportitemproductionDownloading(true));
        const response = await reportitemproductionService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportitemproductionDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportitemproductions:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportitemproductionError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportitemproductionDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportitemproductionPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportitemproductionSort(sort));
};

export const searchReportitemproductions = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportitemproduction.filters;
    dispatch(setReportitemproductionFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportitemproductionDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportitemproductionFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportitemproductionFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportitemproductionFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportitemproductionFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportitemproductionFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportitemproductionFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportitemproduction.settings;
            dispatch(setReportitemproductionSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportitemproductionFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportitemproductionFilterLoading(false));
        dispatch(setReportitemproductionError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportitemproductionSelected(selectedRows));
    };
};

export const clearCurrentReportitemproduction = () => {
    return (dispatch) => {
        dispatch(clearReportitemproductionCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportitemproductionFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportitemproduction.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportitemproductionClassicFilters(updatedClassicFilters));
};